import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp, query, where, orderBy, limit } from "firebase/firestore";
import { getNextMocNumber } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Plus, Trash2, Search, X, ClipboardCheck, UserCheck, CreditCard, CheckCircle, Truck, Save, Send, Loader2, Copy, PackageSearch, AlertCircle } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import { useConfig } from './hooks/useConfig.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';
import { NewWarehouseModal } from './WarehouseModals.js';
import { NewVendorModal } from './VendorModals.js';
import { buildShippingSnapshot, formatAddressFields, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';

import MocDeliveryDetails from './components/MocDeliveryDetails.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return formatAddressFields(addr);
};

const findCustomerRecord = (customers, data = {}) => {
  const customerId = data.customerID || data.customerId || '';
  if (customerId) {
    const matchById = customers.find(customer => customer.id === customerId);
    if (matchById) return matchById;
  }

  if (data.customerName) {
    return customers.find(customer => customer.display_name === data.customerName) || null;
  }

  return null;
};

const findWarehouseRecord = (warehouses, warehouseName) => {
  if (!warehouseName) return null;

  return (
    warehouses.find(warehouse => warehouse.name === warehouseName) ||
    warehouses.find(warehouse => warehouse.name?.toLowerCase().includes(warehouseName.toLowerCase())) ||
    null
  );
};

const getDefaultSalesWarehouseName = (firmId, mocType) => {
  if (mocType !== 'Sales') return '';
  if (firmId === 'nexrise_mh') return 'Bhiwandi Warehouse';
  if (firmId === 'nexrise_rj') return 'Jaipur';
  return '';
};

const getFinancialYearLabel = (dateValue) => {
  const baseDate = dateValue ? new Date(dateValue) : new Date();
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const startYear = month >= 3 ? year : year - 1;
  const endYear = startYear + 1;

  return `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
};

const getTransferNumberPrefix = (dateValue) => `IBT/${getFinancialYearLabel(dateValue)}/`;
const normalizeMultiplierValue = (value) => {
  const numericValue = parseInt(value, 10);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return 1;
  return numericValue;
};

const applyInterbranchMultiplierToComponents = (components = [], multiplierValue = 1) => {
  const multiplier = normalizeMultiplierValue(multiplierValue);

  return components.map((component) => {
    const baseNewQty = Number(component.baseNewQty);
    const baseRentalQty = Number(component.baseRentalQty);
    const nextNewQty = Number.isFinite(baseNewQty)
      ? Math.round(baseNewQty * multiplier)
      : (parseInt(component.newQty, 10) || 0);
    const nextRentalQty = Number.isFinite(baseRentalQty)
      ? Math.round(baseRentalQty * multiplier)
      : (parseInt(component.rentalQty, 10) || 0);

    return {
      ...component,
      newQty: nextNewQty,
      rentalQty: nextRentalQty,
      orderQty: nextNewQty + nextRentalQty
    };
  });
};

const gramsToKg = (weight) => (Number(weight) || 0) / 1000;
const normalizeStoredMocWeight = (weight) => {
  const numericWeight = Number(weight) || 0;
  return numericWeight > 100 ? gramsToKg(numericWeight) : numericWeight;
};
const NON_ALUMINIUM_WEIGHT_SKUS = new Set(['42943', '42913', '42911', '42917-15AJ', '42917-20AJ', '42917-15', '42917-20']);
const calculateAluminiumWeight = (components) => {
  const excludedWeight = components.reduce((sum, component) => {
    if (!NON_ALUMINIUM_WEIGHT_SKUS.has(component.sku)) return sum;

    const rowQty = (parseInt(component.newQty, 10) || 0) + (parseInt(component.rentalQty, 10) || 0);
    return sum + (rowQty * (parseFloat(component.weight) || 0));
  }, 0);

  const totalWeight = components.reduce((sum, component) => {
    const rowQty = (parseInt(component.newQty, 10) || 0) + (parseInt(component.rentalQty, 10) || 0);
    return sum + (rowQty * (parseFloat(component.weight) || 0));
  }, 0);

  return Math.max(0, totalWeight - excludedWeight);
};

const CreateMOCPage = ({ db }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { mocId } = useParams();
  const initialOrderId = location.state?.orderId;

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); 
  const [bomDatabase, setBomDatabase] = useState([]);
  const [formData, setFormData] = useState({
    mocDate: new Date().toISOString().split('T')[0],
    mocNo: '',
    typeOfMOC: '', 
    poNumber: '',
    poDate: '',
    customerName: '',
    shippingAddressId: '',
    shippingAddress: '',
    shippingPersonName: '',
    shippingPersonPhone: '',
    shippingPersonEmail: '',
    shippingSnapshot: null,
    materialCode: '',
    productSummary: '',
    warehouse: '',
    aluminiumWeight: '',
    totalMeterNumber: '',
    interbranchMultiplier: 1,
    components: [],
    orderId: '',
    orderNumber: '',
    firmId: '',
    preparedBy: '',
    quantityVerifiedByDispatch: '',
    vehicleNumber: '',
    vehicleType: '',
    vehicleInTime: '',
    vehicleOutTime: '',
    vehicleDate: '',
    // Finance Fields
    paymentTerms: '',
    paymentStatus: '', 
    lastPaymentAmount: '',
    paymentRecDate: '',
    outstandingAmount: '',
    outstandingDays: '',
    financeRemarks: '',
    // Approval Fields
    approvedByFinance: '',
    approvedBySalesperson: '',
  });

  const [materialChecklistState, setMaterialChecklistState] = useState({
    stickers: false,
    laserMarking: false,
    materialWrapping: false,
    materialInstructions: ''
  });

  // Search state for adding components
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  const [allCustomers, setAllCustomers] = useState([]); 
  const [selectedCustomer, setSelectedCustomer] = useState(null); 

  const [allVendors, setAllVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [allWarehouses, setAllWarehouses] = useState([]);
  const [allFirms, setAllFirms] = useState([]);
  const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState(null); // Source (for Interbranch)
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // Destination

  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showNewWarehouseModal, setShowNewWarehouseModal] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('shipping');
  const [addressDataToEdit, setAddressDataToEdit] = useState(null);

  // Search state for linking orders
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderSuggestions, setOrderSuggestions] = useState([]);
  const [showOrderSuggestions, setShowOrderSuggestions] = useState(false);
  const orderSearchRef = useClickOutside(() => setShowOrderSuggestions(false));

  const [fulfillmentMap, setFulfillmentMap] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const syncShippingSnapshot = (currentData, overrides = {}) => {
    const nextData = { ...currentData, ...overrides };

    if (nextData.typeOfMOC !== 'Sales' && nextData.typeOfMOC !== 'Rental') {
      return { ...nextData, shippingSnapshot: null };
    }

    return {
      ...nextData,
      shippingSnapshot: buildShippingSnapshot({
        customerName: nextData.customerName,
        address: {
          id: nextData.shippingAddressId,
          address: nextData.shippingSnapshot?.address || '',
          city: nextData.shippingSnapshot?.city || '',
          state: nextData.shippingSnapshot?.state || '',
          pincode: nextData.shippingSnapshot?.pincode || '',
          country: nextData.shippingSnapshot?.country || 'India',
          fullAddress: nextData.shippingAddress
        },
        addressId: nextData.shippingAddressId,
        contactName: nextData.shippingPersonName,
        contactPhone: nextData.shippingPersonPhone,
        contactEmail: nextData.shippingPersonEmail
      })
    };
  };

  const applyShippingAddress = (currentData, customerName, addressRecord, contactOverrides = {}) => {
    const snapshot = buildShippingSnapshot({
      customerName,
      address: addressRecord,
      addressId: addressRecord?.id || '',
      contactName: contactOverrides.contactName,
      contactPhone: contactOverrides.contactPhone,
      contactEmail: contactOverrides.contactEmail
    });

    return {
      ...currentData,
      customerName,
      shippingAddressId: snapshot.addressId || '',
      shippingAddress: getShippingSnapshotAddress(snapshot),
      shippingPersonName: snapshot.contactName,
      shippingPersonPhone: snapshot.contactPhone,
      shippingPersonEmail: snapshot.contactEmail,
      shippingSnapshot: snapshot
    };
  };

  const getNextTransferNumber = async (dateValue, currentDocId = null) => {
    const prefix = getTransferNumberPrefix(dateValue);
    const snapshot = await getDocs(
      query(collection(db, "dispatches"), where("typeOfMOC", "==", "Interbranch Transfer"))
    );

    let maxSequence = 999;

    snapshot.docs.forEach((dispatchDoc) => {
      if (dispatchDoc.id === currentDocId) return;

      const transferNumber = dispatchDoc.data().poNumber || '';
      if (!transferNumber.startsWith(prefix)) return;

      const sequence = parseInt(transferNumber.slice(prefix.length), 10);
      if (!Number.isNaN(sequence)) {
        maxSequence = Math.max(maxSequence, sequence);
      }
    });

    return `${prefix}${maxSequence + 1}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const bomSnap = await getDocs(collection(db, "bom_products"));
        const bomList = bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), weight: gramsToKg(doc.data().weight) }));
        setBomDatabase(bomList);
        const weightMap = {};
        bomList.forEach(p => { weightMap[p.sku] = p.weight || 0; });

        // Fetch lists for suggestions
        const [custSnap, pcSnap, mocSnap, whSnap, venSnap, firmSnap] = await Promise.all([
          getDocs(collection(db, "customers")),
          getDocs(query(collection(db, "inwards"), where("status", "==", "pc_ready"), limit(50))),
          getDocs(query(collection(db, "dispatches"), where("dispatchStatus", "==", "delivered"), limit(50))),
          getDocs(collection(db, "warehouses")),
          getDocs(collection(db, "vendors")),
          getDocs(collection(db, "firms"))
        ]);

        const firms = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllFirms(firms);

        const customers = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCustomers(customers);
        const warehouses = whSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllWarehouses(warehouses);
        const vendors = venSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllVendors(vendors);

        if (mocId) {
          const mocDoc = await getDoc(doc(db, "dispatches", mocId));
          if (mocDoc.exists()) {
            const data = mocDoc.data();
            if (['moc_pending_finance', 'pending_ops_approval'].includes(data.dispatchStatus)) {
              navigate(`/ops/moc/preview/${mocId}`);
              return;
            }
            
            // If editing, we still need to know the fulfillment of OTHER MOCs
            if (data.orderId) {
                await calculateFulfillment(data.orderId, mocId);
            }

            const storedMultiplier = normalizeMultiplierValue(data.interbranchMultiplier || 1);
            const normalizedComponents = (data.components || []).map(component => {
              const normalizedComponent = {
                ...component,
                weight: normalizeStoredMocWeight(component.weight)
              };

              if (data.typeOfMOC === 'Interbranch Transfer') {
                const currentNewQty = parseInt(component.newQty, 10) || 0;
                const currentRentalQty = parseInt(component.rentalQty, 10) || 0;
                normalizedComponent.baseNewQty = Number.isFinite(Number(component.baseNewQty))
                  ? Number(component.baseNewQty)
                  : currentNewQty / storedMultiplier;
                normalizedComponent.baseRentalQty = Number.isFinite(Number(component.baseRentalQty))
                  ? Number(component.baseRentalQty)
                  : currentRentalQty / storedMultiplier;
              }

              return normalizedComponent;
            });
            const hydratedData = {
              ...formData,
              ...data,
              id: mocDoc.id,
              interbranchMultiplier: storedMultiplier,
              components: data.typeOfMOC === 'Interbranch Transfer'
                ? applyInterbranchMultiplierToComponents(normalizedComponents, storedMultiplier)
                : normalizedComponents
            };
            const normalizedData = (data.typeOfMOC === 'Sales' || data.typeOfMOC === 'Rental') && data.shippingSnapshot
              ? {
                  ...hydratedData,
                  shippingAddressId: data.shippingSnapshot.addressId || hydratedData.shippingAddressId || '',
                  shippingAddress: getShippingSnapshotAddress(data.shippingSnapshot, hydratedData.shippingAddress || ''),
                  shippingPersonName: data.shippingSnapshot.contactName || hydratedData.shippingPersonName || '',
                  shippingPersonPhone: data.shippingSnapshot.contactPhone || hydratedData.shippingPersonPhone || '',
                  shippingPersonEmail: data.shippingSnapshot.contactEmail || hydratedData.shippingPersonEmail || '',
                  shippingSnapshot: buildShippingSnapshot(data.shippingSnapshot)
                }
              : hydratedData;
            setFormData(normalizedData);
            setOrderSearchTerm(data.orderNumber || '');
            if (data.materialChecklist) {
              setMaterialChecklistState(data.materialChecklist);
            }

            // Set selected objects
            const matchedCustomer = findCustomerRecord(customers, data);
            if (matchedCustomer) {
                setSelectedCustomer(matchedCustomer);
            }
            if (data.vendorID) {
                setSelectedVendor(vendors.find(v => v.id === data.vendorID));
            }
            if (data.sourceWarehouseID) {
                setSelectedSourceWarehouse(warehouses.find(w => w.id === data.sourceWarehouseID));
            }
            if (data.warehouse) {
                setSelectedWarehouse(findWarehouseRecord(warehouses, data.warehouse));
            }
          }
        } else if (initialOrderId) {
            await calculateFulfillment(initialOrderId);
            await handleOrderSelection(initialOrderId, weightMap, { customers, warehouses, vendors });
        } else {
            // New independent MOC
            const generatedMocNo = await getNextMocNumber(db);
            setFormData(prev => ({ ...prev, mocNo: generatedMocNo }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, mocId, initialOrderId]);

  useEffect(() => {
    const syncTransferNumber = async () => {
      if (formData.typeOfMOC !== 'Interbranch Transfer') return;
      if (mocId && formData.poNumber) return;
      if (initialOrderId) return;

      const nextTransferNumber = await getNextTransferNumber(formData.mocDate, mocId);
      setFormData(prev => {
        if (prev.typeOfMOC !== 'Interbranch Transfer') return prev;
        if (prev.poNumber === nextTransferNumber) return prev;
        return { ...prev, poNumber: nextTransferNumber };
      });
    };

    syncTransferNumber();
  }, [formData.typeOfMOC, formData.mocDate, mocId, initialOrderId]);

  const calculateFulfillment = async (orderId, currentMocId = null) => {
      const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(doc => {
          if (doc.id === currentMocId) return; // Don't count quantities from the MOC we are currently editing
          const data = doc.data();
          if (data.dispatchStatus === 'packing_rejected') return; // Ignore rejected
          
          data.components?.forEach(c => {
              if (!map[c.sku]) map[c.sku] = { new: 0, rental: 0 };
              map[c.sku].new += (parseInt(c.newQty) || 0);
              map[c.sku].rental += (parseInt(c.rentalQty) || 0);
          });
      });
      setFulfillmentMap(map);
      return map;
  };

  const handleOrderSelection = async (orderId, weightMapOverride = null, preFetched = {}) => {
    try {
      const orderDoc = await getDoc(doc(db, "orders", orderId));
      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        const weightMap = weightMapOverride || bomDatabase.reduce((acc, p) => ({ ...acc, [p.sku]: p.weight || 0 }), {});
        
        // Refresh fulfillment map just in case
        const currentFulfillment = await calculateFulfillment(orderId);

        const initialComponents = [];
        const isRentalOrder = orderData.orderType === 'Rental';

        if (orderData.boms) {
          orderData.boms.forEach(bom => {
            bom.components.forEach(comp => {
              const existing = initialComponents.find(c => c.sku === comp.sku);
              const qty = Math.round(comp.baseQty * (bom.multiplier || 1));
              
              const prevNew = currentFulfillment[comp.sku]?.new || 0;
              const prevRental = currentFulfillment[comp.sku]?.rental || 0;
              const remainingQty = Math.max(0, qty - (isRentalOrder ? prevRental : prevNew));

              if (existing) {
                if (isRentalOrder) {
                    existing.maxOrderQty += qty;
                    existing.rentalQty += remainingQty;
                    existing.orderQty += remainingQty;
                } else {
                    existing.maxOrderQty += qty;
                    existing.newQty += remainingQty;
                    existing.orderQty += remainingQty;
                }
              } else {
                initialComponents.push({
                  id: comp.id || `${comp.sku}-${Date.now()}`,
                  sku: comp.sku,
                  name: comp.name,
                  orderQty: remainingQty,
                  maxOrderQty: qty,
                  newQty: isRentalOrder ? 0 : remainingQty,
                  rentalQty: isRentalOrder ? remainingQty : 0,
                  weight: weightMap[comp.sku] || 0,
                  ticked: remainingQty > 0
                });
              }
            });
          });
        }

        const suggestedWarehouseName = getDefaultSalesWarehouseName(orderData.firmId, orderData.orderType);
        const matchedCustomer = findCustomerRecord(preFetched.customers || allCustomers, orderData);

        const generatedMocNo = formData.mocNo || await getNextMocNumber(db);

        setFormData(prev => {
          const baseData = applyShippingAddress(
            prev,
            matchedCustomer?.display_name || orderData.customerName || '',
            orderData.shippingAddress
          );

          return {
            ...baseData,
            mocNo: generatedMocNo,
            typeOfMOC: orderData.orderType || prev.typeOfMOC,
            poNumber: (orderData.orderType === 'Rental' ? orderData.workOrderNumber : orderData.poNumber) || '',
            poDate: (orderData.orderType === 'Rental' ? orderData.woDate : orderData.poDate) || '',
            customerID: matchedCustomer?.id || orderData.customerId || orderData.customerID || '',
            productSummary: orderData.boms?.map(b => b.name).join(', ') || '',
            components: initialComponents,
            warehouse: suggestedWarehouseName,
            orderId: orderId,
            orderNumber: orderData.orderNumber || '',
            firmId: orderData.firmId || '',
            paymentTerms: orderData.paymentTerms || '',
            paymentStatus: orderData.paymentStatus || '',
            lastPaymentAmount: orderData.lastPaymentAmount || '',
            paymentRecDate: orderData.paymentRecDate || '',
            outstandingAmount: orderData.outstandingAmount || '',
            outstandingDays: orderData.outstandingDays || '',
            financeRemarks: orderData.financeRemarks || '',
            approvedByFinance: orderData.verifiedBy || '',
            approvedBySalesperson: orderData.salesPerson || '',
          };
        });

        if (orderData.materialChecklist) {
            setMaterialChecklistState(orderData.materialChecklist);
        }

        // Update selected objects for the component
        const warehouses = preFetched.warehouses || allWarehouses;
        setSelectedCustomer(matchedCustomer || null);
        setSelectedWarehouse(findWarehouseRecord(warehouses, suggestedWarehouseName));

        setOrderSearchTerm(orderData.orderNumber || '');
        setShowOrderSuggestions(false);
      }
    } catch (error) {
      console.error("Error selecting order:", error);
    }
  };

  const handleOrderSearch = async (val) => {
    setOrderSearchTerm(val);
    if (val.length > 1) {
        try {
            const q = query(
                collection(db, "orders"),
                where("status", "in", ["in_que", "confirmed_order", "moc_in_progress"]),
                limit(20)
            );
            const snap = await getDocs(q);
            const matches = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(o => 
                    o.orderNumber?.toLowerCase().includes(val.toLowerCase()) || 
                    o.customerName?.toLowerCase().includes(val.toLowerCase())
                );
            setOrderSuggestions(matches);
            setShowOrderSuggestions(true);
        } catch (e) {
            console.error("Order search error:", e);
        }
    } else {
        setOrderSuggestions([]);
        setShowOrderSuggestions(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    const firstShipping = customer.addresses?.find(a => a.address_type === 'Shipping');
    setFormData(prev => ({
      ...applyShippingAddress(prev, customer.display_name, firstShipping || null),
      customerID: customer.id
    }));
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({ 
        ...prev, 
        customerName: vendor.name, 
        vendorID: vendor.id,
        shippingAddress: vendor.address ? `${vendor.address}, ${vendor.city || ''}, ${vendor.state || ''} - ${vendor.pincode || ''}` : ''
    }));
  };

  const selectSourceWarehouse = (warehouse) => {
    setSelectedSourceWarehouse(warehouse);
    setFormData(prev => ({ 
        ...prev, 
        customerName: warehouse.name, 
        sourceWarehouseID: warehouse.id,
        shippingAddress: warehouse.address ? `${warehouse.address}, ${warehouse.city || ''}, ${warehouse.state || ''} - ${warehouse.pincode || ''}` : ''
    }));
  };

  const selectWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setFormData(prev => ({ 
      ...prev, 
      warehouse: warehouse.name,
      sourceWarehouseID: warehouse.id
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValidationErrors(prev => {
      if (!prev[name]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[name];
      return nextErrors;
    });
    setFormData(prev => syncShippingSnapshot(prev, { [name]: value }));
  };

  const handleInterbranchMultiplierChange = (value) => {
    const multiplier = normalizeMultiplierValue(value);

    setFormData((prev) => ({
      ...prev,
      interbranchMultiplier: multiplier,
      components: applyInterbranchMultiplierToComponents(prev.components, multiplier)
    }));
  };

  const getFieldErrorStyle = (fieldName, baseStyle = inputStyle) => (
    validationErrors[fieldName]
      ? { ...baseStyle, border: '1px solid #dc2626', boxShadow: '0 0 0 1px #dc2626' }
      : baseStyle
  );

  const validateFinanceSubmission = () => {
    const nextErrors = {};
    const aluminiumWeightValue = calculateAluminiumWeight(formData.components);

    if (!String(formData.materialCode || '').trim()) nextErrors.materialCode = true;
    if (!String(formData.warehouse || '').trim()) nextErrors.warehouse = true;
    if (!String(formData.customerName || '').trim()) nextErrors.customerName = true;
    if (!String(formData.preparedBy || '').trim()) nextErrors.preparedBy = true;
    if (!String(aluminiumWeightValue).trim()) nextErrors.aluminiumWeight = true;
    if (!String(formData.totalMeterNumber || '').trim()) nextErrors.totalMeterNumber = true;

    setValidationErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      alert('Please fill all required fields highlighted in red before submitting for approval.');
      return false;
    }

    return true;
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...formData.components];
    const comp = updated[index];
    
    if (field === 'newQty' || field === 'rentalQty') {
        const val = parseInt(value) || 0;
        const currentNew = field === 'newQty' ? val : (comp.newQty || 0);
        const currentRental = field === 'rentalQty' ? val : (comp.rentalQty || 0);

        // Allow unlimited quantities for Interbranch Transfer MOCs.
        if (formData.typeOfMOC === 'Interbranch Transfer') {
            const multiplier = normalizeMultiplierValue(formData.interbranchMultiplier);
            updated[index][field] = val;
            updated[index].baseNewQty = currentNew / multiplier;
            updated[index].baseRentalQty = currentRental / multiplier;
            updated[index].orderQty = currentNew + currentRental;
            setFormData(prev => ({ ...prev, components: updated }));
            return;
        }
        
        const limit = comp.maxOrderQty || comp.orderQty || 0;
        // Combined overshipping logic: (Sum of New/Rental in this MOC) + (Sum of New/Rental in other MOCs) <= Order Qty
        const prevMocdTotal = (fulfillmentMap[comp.sku]?.new || 0) + (fulfillmentMap[comp.sku]?.rental || 0);
        
        const totalWithThisChange = prevMocdTotal + currentNew + currentRental;
        const maxAllowedForThisField = Math.max(0, limit - prevMocdTotal - (field === 'newQty' ? (comp.rentalQty || 0) : (comp.newQty || 0)));

        if (totalWithThisChange > limit) {
            alert(`Combined Quantity (New + Rental) cannot exceed Order Quantity (${limit}). Max allowed for this field: ${maxAllowedForThisField}`);
            updated[index][field] = maxAllowedForThisField;
        } else {
            updated[index][field] = val;
        }
        
        // Always sync orderQty to current sum
        updated[index].orderQty = (parseInt(updated[index].newQty || 0) + parseInt(updated[index].rentalQty || 0));
    } else if (field === 'weight') {
        updated[index][field] = parseFloat(value) || 0;
    } else {
        updated[index][field] = value;
    }
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const resolveProductSelection = (product) => {
    if (product.type === 'preset') {
      return (product.presetComponents || [])
        .map(pc => {
          const component = bomDatabase.find(item => item.sku === pc.sku);
          if (!component) return null;

          return {
            ...component,
            presetQty: parseInt(pc.qty, 10) || 0
          };
        })
        .filter(Boolean);
    }

    return [{ ...product, presetQty: 1 }];
  };

  const addComponent = (product) => {
    const selectedProducts = resolveProductSelection(product);
    const isRental = formData.typeOfMOC === 'Rental';
    const isInterbranch = formData.typeOfMOC === 'Interbranch Transfer';
    const multiplier = normalizeMultiplierValue(formData.interbranchMultiplier);
    const qtyField = isRental ? 'rentalQty' : 'newQty';

    setFormData(prev => {
      const updatedComponents = [...prev.components];

      selectedProducts.forEach(selectedProduct => {
        const qtyToAdd = selectedProduct.presetQty || 1;
        const existingIndex = updatedComponents.findIndex(c => c.sku === selectedProduct.sku);

        if (existingIndex > -1) {
          if (isInterbranch) {
            const nextBaseNewQty = (Number(updatedComponents[existingIndex].baseNewQty) || 0) + qtyToAdd;
            const nextBaseRentalQty = Number(updatedComponents[existingIndex].baseRentalQty) || 0;
            updatedComponents[existingIndex] = {
              ...updatedComponents[existingIndex],
              baseNewQty: nextBaseNewQty,
              baseRentalQty: nextBaseRentalQty,
              newQty: Math.round(nextBaseNewQty * multiplier),
              rentalQty: Math.round(nextBaseRentalQty * multiplier),
              orderQty: Math.round(nextBaseNewQty * multiplier) + Math.round(nextBaseRentalQty * multiplier)
            };
            return;
          }

          updatedComponents[existingIndex] = {
            ...updatedComponents[existingIndex],
            [qtyField]: (parseInt(updatedComponents[existingIndex][qtyField], 10) || 0) + qtyToAdd
          };
          return;
        }

        updatedComponents.push({
          id: `${product.sku}-${selectedProduct.sku}-${Date.now()}`,
          sku: selectedProduct.sku,
          name: selectedProduct.name,
          orderQty: isInterbranch ? Math.round(qtyToAdd * multiplier) : qtyToAdd,
          maxOrderQty: selectedProduct.presetQty || qtyToAdd,
          newQty: isInterbranch ? Math.round(qtyToAdd * multiplier) : (isRental ? 0 : qtyToAdd),
          rentalQty: isInterbranch ? 0 : (isRental ? qtyToAdd : 0),
          baseNewQty: isInterbranch ? qtyToAdd : undefined,
          baseRentalQty: isInterbranch ? 0 : undefined,
          weight: selectedProduct.weight || 0,
          ticked: false
        });
      });

      return { ...prev, components: updatedComponents };
    });

    setSearchTerm('');
    setFilteredProducts([]);
    setShowSuggestions(false);
  };

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.sku.toLowerCase().includes(term)
      ).slice(0, 10);
      setFilteredProducts(matches);
      setShowSuggestions(true);
    } else {
      setFilteredProducts([]);
      setShowSuggestions(false);
    }
  };

  const handleClone = () => {
      const clonedData = { 
          ...formData, 
          mocNo: '', 
          mocDate: new Date().toISOString().split('T')[0] 
      };
      navigate('/ops/moc/new', { state: { orderId: formData.orderId, initialData: clonedData } });
  };

  const totals = useMemo(() => {
    return formData.components.reduce((acc, c) => {
      const rowQty = (parseInt(c.newQty) || 0) + (parseInt(c.rentalQty) || 0);
      acc.newQty += (parseInt(c.newQty) || 0);
      acc.rentalQty += (parseInt(c.rentalQty) || 0);
      acc.weight += rowQty * (parseFloat(c.weight) || 0);
      return acc;
    }, { newQty: 0, rentalQty: 0, weight: 0 });
  }, [formData.components]);
  const aluminiumWeightValue = useMemo(() => calculateAluminiumWeight(formData.components), [formData.components]);

  const saveMocDraft = async () => {
    if (!formData.mocNo) {
      alert("MOC Number is required to save a draft.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        firmId: formData.firmId || '',
        aluminiumWeight: aluminiumWeightValue,
        materialChecklist: materialChecklistState,
        totalWeight: totals.weight,
        dispatchStatus: formData.dispatchStatus === 'moc_rejected' ? 'moc_rejected' : 'moc_draft',
        updatedAt: serverTimestamp(),
        createdAt: mocId ? formData.createdAt : serverTimestamp(),
      };

      if (formData.typeOfMOC === 'Sales' || formData.typeOfMOC === 'Rental') {
        payload.shippingSnapshot = buildShippingSnapshot({
          customerName: formData.customerName,
          address: {
            id: formData.shippingAddressId,
            address: formData.shippingSnapshot?.address || '',
            city: formData.shippingSnapshot?.city || '',
            state: formData.shippingSnapshot?.state || '',
            pincode: formData.shippingSnapshot?.pincode || '',
            country: formData.shippingSnapshot?.country || 'India',
            fullAddress: formData.shippingAddress
          },
          addressId: formData.shippingAddressId,
          contactName: formData.shippingPersonName,
          contactPhone: formData.shippingPersonPhone,
          contactEmail: formData.shippingPersonEmail
        });
      } else {
        delete payload.shippingSnapshot;
      }

      if (formData.typeOfMOC === 'Interbranch Transfer') {
        payload.destinationWarehouseName = formData.customerName;
        payload.destinationWarehouseAddress = formData.shippingAddress;
        payload.destinationContactName = formData.shippingPersonName;
        payload.destinationContactEmail = formData.shippingPersonEmail;
        payload.destinationContactPhone = formData.shippingPersonPhone;
      }

      if (mocId) {
        await updateDoc(doc(db, "dispatches", mocId), payload);
      } else {
        const docRef = await addDoc(collection(db, "dispatches"), payload);
        if (formData.orderId) {
          await updateDoc(doc(db, "orders", formData.orderId), { status: 'moc_in_progress' });
        }
      }
      alert("Draft Saved Successfully");
      navigate('/ops/shipments');
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveMocConfirm = async () => {
    if (!formData.mocNo) {
      alert("MOC Number is required to submit.");
      return;
    }

    const isInterbranchTransfer = formData.typeOfMOC === 'Interbranch Transfer';
    if (!isInterbranchTransfer && !validateFinanceSubmission()) {
      return;
    }
    const targetDispatchStatus = 'pending_ops_approval';
    const targetOrderStatus = 'pending_ops_approval';
    const confirmMessage = "Are you sure you want to SUBMIT this MOC for approval?";

    if (!window.confirm(confirmMessage)) return;
    
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        aluminiumWeight: aluminiumWeightValue,
        materialChecklist: materialChecklistState,
        totalWeight: totals.weight,
        dispatchStatus: targetDispatchStatus,
        updatedAt: serverTimestamp(),
        createdAt: mocId ? formData.createdAt : serverTimestamp(),
      };

      if (formData.typeOfMOC === 'Sales' || formData.typeOfMOC === 'Rental') {
        payload.shippingSnapshot = buildShippingSnapshot({
          customerName: formData.customerName,
          address: {
            id: formData.shippingAddressId,
            address: formData.shippingSnapshot?.address || '',
            city: formData.shippingSnapshot?.city || '',
            state: formData.shippingSnapshot?.state || '',
            pincode: formData.shippingSnapshot?.pincode || '',
            country: formData.shippingSnapshot?.country || 'India',
            fullAddress: formData.shippingAddress
          },
          addressId: formData.shippingAddressId,
          contactName: formData.shippingPersonName,
          contactPhone: formData.shippingPersonPhone,
          contactEmail: formData.shippingPersonEmail
        });
      } else {
        delete payload.shippingSnapshot;
      }

      if (isInterbranchTransfer) {
        payload.destinationWarehouseName = formData.customerName;
        payload.destinationWarehouseAddress = formData.shippingAddress;
        payload.destinationContactName = formData.shippingPersonName;
        payload.destinationContactEmail = formData.shippingPersonEmail;
        payload.destinationContactPhone = formData.shippingPersonPhone;
      }

      if (mocId) {
        await updateDoc(doc(db, "dispatches", mocId), payload);
      } else {
        await addDoc(collection(db, "dispatches"), payload);
      }

      // --- AUTO-CREATE DRAFT FOR REMAINING ---
      if (formData.orderId && targetDispatchStatus === 'pending_ops_approval') {
          const remainingComponents = formData.components.map(c => {
              const limit = c.maxOrderQty || c.orderQty || 0;
              const prevMocd = (formData.typeOfMOC === 'Rental') 
                  ? (fulfillmentMap[c.sku]?.rental || 0) 
                  : (fulfillmentMap[c.sku]?.new || 0);
              
              const currentMocd = (formData.typeOfMOC === 'Rental') ? (c.rentalQty || 0) : (c.newQty || 0);
              const totalMocd = prevMocd + currentMocd;
              const balance = Math.max(0, limit - totalMocd);

              if (balance > 0) {
                  return {
                      ...c,
                      newQty: formData.typeOfMOC === 'Rental' ? 0 : balance,
                      rentalQty: formData.typeOfMOC === 'Rental' ? balance : 0,
                      ticked: true
                  };
              }
              return null;
          }).filter(c => c !== null);

          if (remainingComponents.length > 0) {
              const nextMocNo = await getNextMocNumber(db);
              const draftPayload = {
                  ...formData,
                  mocNo: nextMocNo,
                  mocDate: new Date().toISOString().split('T')[0],
                  components: remainingComponents,
                  dispatchStatus: 'moc_draft',
                  updatedAt: serverTimestamp(),
                  createdAt: serverTimestamp(),
              };
              delete draftPayload.id;
              await addDoc(collection(db, "dispatches"), draftPayload);
              console.log("Auto-created draft MOC for balance:", nextMocNo);
          }

          await updateDoc(doc(db, "orders", formData.orderId), { status: targetOrderStatus });
      } else if (formData.orderId) {
          await updateDoc(doc(db, "orders", formData.orderId), { status: targetOrderStatus });
      }

      const successMessage = "MOC Submitted for Approval Successfully.";

      alert(successMessage);
      navigate('/ops/shipments');
    } catch (error) {
      console.error("Error submitting MOC:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const isSalesOrRental = formData.typeOfMOC === 'Sales' || formData.typeOfMOC === 'Rental';
  const isInterbranchTransfer = formData.typeOfMOC === 'Interbranch Transfer';
  const isMocNumberReadOnly = isSalesOrRental || isInterbranchTransfer;
  const isSalesRentalHeaderReadOnly = isSalesOrRental;
  let poLabel = "PO Number";
  let dateLabel = "PO Date";

  if (formData.typeOfMOC === 'Rental') {
    poLabel = "WO Number";
    dateLabel = "WO Date";
  } else if (!isSalesOrRental) {
    poLabel = "Transfer Number";
    dateLabel = "Transfer Req. Date";
  }

  const latestOpsReview = useMemo(() => {
    if (Array.isArray(formData.opsReviewHistory) && formData.opsReviewHistory.length > 0) {
      return formData.opsReviewHistory[formData.opsReviewHistory.length - 1];
    }

    if (String(formData.opsReviewRemarks || '').trim()) {
      return {
        remark: formData.opsReviewRemarks,
        reviewedBy: formData.opsReviewedBy || '',
        reviewedAt: formData.opsReviewedAt || '',
        status: formData.opsReviewStatus || formData.dispatchStatus || ''
      };
    }

    return null;
  }, [formData.opsReviewHistory, formData.opsReviewRemarks, formData.opsReviewedBy, formData.opsReviewedAt, formData.opsReviewStatus, formData.dispatchStatus]);

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {formData.dispatchStatus === 'moc_rejected' && latestOpsReview && (
        <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ea580c" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#9a3412', fontSize: '16px' }}>MOC Returned by Ops Manager</h3>
            <p style={{ margin: 0, color: '#7c2d12', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
              <strong>Remarks:</strong> {latestOpsReview.remark || 'No specific remarks provided.'}
            </p>
            <p style={{ margin: '10px 0 0 0', color: '#9a3412', fontSize: '13px' }}>
              {latestOpsReview.reviewedAt ? `Reviewed on ${new Date(latestOpsReview.reviewedAt).toLocaleString()}` : 'The Ops team needs to revise the MOC and submit it again for approval.'}
              {latestOpsReview.reviewedBy ? ` • ${latestOpsReview.reviewedBy}` : ''}
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0 }}>{mocId ? 'Edit' : 'Create'} Material Out Checklist</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {isSaving && <Loader2 className="animate-spin" size={24} color="#2563eb" />}
            {isInterbranchTransfer && (
              <button onClick={handleClone} style={secondaryBtnStyle} title="Clone to new draft"><Copy size={18}/> Clone</button>
            )}
            <button onClick={saveMocDraft} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
            <button onClick={saveMocConfirm} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Submit for Approval</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            <div><label style={labelStyle}>Date</label><input type="date" name="mocDate" value={formData.mocDate} onChange={handleChange} style={{ ...inputStyle, backgroundColor: '#f8fafc' }} readOnly /></div>
            <div>
              <label style={labelStyle}>MOC no.</label>
              <input 
                type="text" 
                name="mocNo" 
                value={formData.mocNo || ''} 
                onChange={handleChange} 
                style={{ ...inputStyle, backgroundColor: isMocNumberReadOnly ? '#f8fafc' : '#fff' }} 
                placeholder={loading ? "Loading..." : "MOC Number"}
                readOnly={isMocNumberReadOnly}
              />
            </div>
            <div>
                <label style={labelStyle}>Type of MOC</label>
                <select 
                    name="typeOfMOC" 
                    value={formData.typeOfMOC} 
                    onChange={handleChange} 
                    style={{ ...inputStyle, backgroundColor: isSalesRentalHeaderReadOnly ? '#f8fafc' : '#fff' }}
                    disabled={isSalesRentalHeaderReadOnly}
                >
                    <option value="">Select Type...</option>
                    {config.moc_types.filter(opt => !['Repair', 'Demo', 'Scrap', 'Demo/Exhibition'].includes(opt)).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>
            <div>
                <label style={labelStyle}>Firm</label>
                <select 
                    name="firmId" 
                    value={formData.firmId} 
                    onChange={handleChange} 
                    style={{ ...inputStyle, backgroundColor: isSalesRentalHeaderReadOnly ? '#f8fafc' : '#fff' }}
                    disabled={isSalesRentalHeaderReadOnly}
                >
                    <option value="">Select Firm...</option>
                    {allFirms.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                </select>
            </div>

            {isSalesOrRental && (
                <div style={{ gridColumn: 'span 4', position: 'relative' }} ref={orderSearchRef}>
                    <label style={labelStyle}>Link Order (Search by Number or Name)</label>
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                        <PackageSearch size={18} color="#64748b" />
                        <input 
                            type="text" 
                            placeholder="Find order to populate data..." 
                            value={orderSearchTerm} 
                            onChange={(e) => handleOrderSearch(e.target.value)} 
                            onFocus={() => !isSalesRentalHeaderReadOnly && orderSearchTerm.length > 1 && setShowOrderSuggestions(true)}
                            style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px', backgroundColor: isSalesRentalHeaderReadOnly ? '#f8fafc' : '#fff' }}
                            readOnly={isSalesRentalHeaderReadOnly}
                        />
                        {formData.orderId && <CheckCircle size={18} color="#10b981" style={{ marginLeft: '10px' }} />}
                    </div>
                    {showOrderSuggestions && !isSalesRentalHeaderReadOnly && (
                        <div style={suggestionsBoxStyle}>
                            {orderSuggestions.map(o => (
                                <div key={o.id} onClick={() => handleOrderSelection(o.id)} style={suggestionItemStyle}>
                                    <div style={{ fontWeight: 'bold' }}>{o.orderNumber}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{o.customerName} - {o.subject}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div>
              <label style={labelStyle}>{poLabel}</label>
              <input
                type="text"
                name="poNumber"
                value={formData.poNumber}
                onChange={handleChange}
                style={{ ...inputStyle, backgroundColor: (isSalesRentalHeaderReadOnly || isInterbranchTransfer) ? '#f8fafc' : '#fff' }}
                readOnly={isSalesRentalHeaderReadOnly || isInterbranchTransfer}
              />
            </div>
            <div><label style={labelStyle}>{dateLabel}</label><input type="date" name="poDate" value={formData.poDate} onChange={handleChange} style={{ ...inputStyle, backgroundColor: isSalesRentalHeaderReadOnly ? '#f8fafc' : '#fff' }} readOnly={isSalesRentalHeaderReadOnly} /></div>
            <div><label style={labelStyle}>Material Code</label><input type="text" name="materialCode" value={formData.materialCode} onChange={handleChange} style={getFieldErrorStyle('materialCode')} placeholder="Enter code..." /></div>
            
            <div>
                <label style={labelStyle}>Salesperson {isSalesOrRental ? '(Read-Only)' : ''}</label>
                <input 
                    type="text" 
                    name="approvedBySalesperson"
                    value={formData.approvedBySalesperson} 
                    onChange={handleChange}
                    style={{ ...inputStyle, backgroundColor: isSalesOrRental ? '#f8fafc' : '#fff' }} 
                    disabled={isSalesOrRental}
                    placeholder="Enter salesperson name..."
                />
            </div>
          </div>
        </div>

        <MocDeliveryDetails
          formData={formData}
          setFormData={setFormData}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          allCustomers={allCustomers}
          allWarehouses={allWarehouses}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          selectedWarehouse={selectedWarehouse}
          setSelectedWarehouse={setSelectedWarehouse}
          setShowNewCustomerModal={setShowNewCustomerModal}
          setShowNewWarehouseModal={setShowNewWarehouseModal}
          setShowAddAddressModal={setShowAddAddressModal}
          setAddressModalType={setAddressModalType}
          setAddressDataToEdit={setAddressDataToEdit}
          sectionTitle="Consignee & Delivery Details"
        />
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>BOM:</h3>
            <input 
                type="text" 
                name="productSummary" 
                value={formData.productSummary} 
                onChange={handleChange} 
                placeholder="BOM Description from order..."
                style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1e293b', 
                    border: 'none', 
                    borderBottom: '1px dashed #cbd5e1', 
                    width: '100%', 
                    padding: '4px 0',
                    outline: 'none'
                }} 
            />
          </div>

          {isInterbranchTransfer && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', marginLeft: '20px' }}>
              <div style={{ minWidth: '140px' }}>
                <label style={labelStyle}>Multiplier</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.interbranchMultiplier || 1}
                  onChange={(e) => handleInterbranchMultiplierChange(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          )}
          
          <div style={{ position: 'relative', width: '300px', marginLeft: '20px' }} ref={searchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search presets or components..." 
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }}
              />
            </div>
            {showSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredProducts.map(p => (
                  <div key={p.id} onClick={() => addComponent(p)} style={suggestionItemStyle}>
                    <strong>{p.sku}</strong> - {p.name}
                    <span style={{ textTransform: 'capitalize', color: '#6b7280', fontSize: '12px' }}> ({p.type || 'component'})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tableStyle, tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ ...thStyle, width: '50px' }}>S.No.</th>
                <th style={{ ...thStyle, width: '100px' }}>SKU</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Component Name</th>
                <th style={{ ...thStyle, width: '80px' }}>New</th>
                <th style={{ ...thStyle, width: '80px' }}>Rental</th>
                <th style={{ ...thStyle, width: '100px' }}>Weight</th>
                <th style={{ ...thStyle, width: '100px' }}>Total Weight</th>
                <th style={{ ...thStyle, width: '60px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.components.map((c, index) => {
                const totalRowWeight = ( (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * (parseFloat(c.weight) || 0) ).toFixed(2);
                
                return (
                  <tr key={c.id || index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{c.sku}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</td>
                    <td style={tdStyle}>
                        <input 
                          type="number" 
                          value={c.newQty} 
                          onChange={(e) => handleComponentChange(index, 'newQty', e.target.value)} 
                          style={{ ...inputStyle, width: '60px', textAlign: 'center', padding: '5px' }}
                        />
                    </td>
                    <td style={tdStyle}>
                        <input 
                          type="number" 
                          value={c.rentalQty} 
                          onChange={(e) => handleComponentChange(index, 'rentalQty', e.target.value)} 
                          style={{ ...inputStyle, width: '60px', textAlign: 'center', padding: '5px' }}
                        />
                    </td>
                    <td style={tdStyle}>
                      {(parseFloat(c.weight) || 0).toFixed(2)}
                    </td>
                    <td style={tdStyle}>{totalRowWeight}</td>
                    <td style={tdStyle}>
                      <button onClick={() => deleteComponent(index)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                <td colSpan="3" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL SUMMARY</td>
                <td style={tdStyle}>{totals.newQty}</td>
                <td style={tdStyle}>{totals.rentalQty}</td>
                <td style={tdStyle}>{totals.weight.toFixed(2)} kg</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><ClipboardCheck size={18} /> Material Checklist</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.stickers} onChange={(e) => setMaterialChecklistState({...materialChecklistState, stickers: e.target.checked})} style={{ marginRight: '10px' }} />
                        Stickers
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.laserMarking} onChange={(e) => setMaterialChecklistState({...materialChecklistState, laserMarking: e.target.checked})} style={{ marginRight: '10px' }} />
                        Laser Marking
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.materialWrapping} onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialWrapping: e.target.checked})} style={{ marginRight: '10px' }} />
                        Material Wrapping
                    </label>
                </div>
                <textarea 
                    name="materialInstructions" 
                    value={materialChecklistState.materialInstructions} 
                    onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialInstructions: e.target.value})} 
                    placeholder="Please add any additional instructions here" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><Truck size={18} /> Physical Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Aluminium Weight (kg)</label>
                        <input type="number" name="aluminiumWeight" value={aluminiumWeightValue.toFixed(2)} readOnly style={{ ...getFieldErrorStyle('aluminiumWeight'), backgroundColor: '#f8fafc' }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Total Meter Number</label>
                        <input type="number" step="0.01" name="totalMeterNumber" value={formData.totalMeterNumber} onChange={handleChange} style={getFieldErrorStyle('totalMeterNumber')} />
                    </div>
                </div>
            </div>

            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><UserCheck size={18} /> Verification</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={labelStyle}>Prepared By</label>
                        <select 
                            name="preparedBy" 
                            value={formData.preparedBy} 
                            onChange={handleChange} 
                            style={getFieldErrorStyle('preparedBy')}
                        >
                            <option value="">Select Staff...</option>
                            {(config.field_staff?.length ? config.field_staff : config.moc_prepared_by).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Modals for Customer and Address Management */}
      <NewCustomerModal
        show={showNewCustomerModal}
        onClose={() => setShowNewCustomerModal(false)}
        onSave={(savedCust) => {
            setAllCustomers(prev => [...prev, savedCust]);
            selectCustomer(savedCust);
            setShowNewCustomerModal(false);
        }}
        db={db}
        isMobile={false} 
      />

      {selectedCustomer && (
        <AddAddressModal
            show={showAddAddressModal}
            onClose={() => setShowAddAddressModal(false)}
            onSave={(savedAddress, updatedAddresses) => {
                const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
                setSelectedCustomer(updatedCustomer); 
                setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)); 
                setFormData(prev => ({
                  ...applyShippingAddress(prev, updatedCustomer.display_name, savedAddress),
                  customerID: updatedCustomer.id
                }));
                setShowAddAddressModal(false);
            }}
            db={db}
            customer={selectedCustomer}
            addressType={addressModalType}
            addressData={addressDataToEdit}
            isMobile={false} 
            hideBillingOption={true}
        />
      )}

      <NewWarehouseModal
        show={showNewWarehouseModal}
        onClose={() => setShowNewWarehouseModal(false)}
        onSave={(savedWh) => {
          setAllWarehouses(prev => [...prev, savedWh]);
          setFormData(prev => ({ ...prev, warehouse: savedWh.name }));
          setSelectedWarehouse(savedWh);
          setShowNewWarehouseModal(false);
        }}
        db={db}
      />

      <NewVendorModal
        show={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        onSave={(savedVen) => {
          setAllVendors(prev => [...prev, savedVen]);
          selectVendor(savedVen);
          setShowNewVendorModal(false);
        }}
        db={db}
      />
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const radioLabelStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' };
const saveBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' };
const draftBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', display: 'flex', alignItems: 'center', gap: '8px' };
const confirmBtnStyle = { ...saveBtnStyle, backgroundColor: '#059669', display: 'flex', alignItems: 'center', gap: '8px' };
const secondaryBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#64748b', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', fontSize: '13px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4, padding: 0 };

export default CreateMOCPage;
